import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { localRect, localViewport } from '../../utils/zoom';

const GAP = 6;
const EDGE = 8;

// Hover/focus tooltip, portalled to <body> so a card's `overflow: hidden`
// never clips it. Wraps a single focusable trigger element.
function Tooltip({ content, children, side = 'top' }) {
  const triggerRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const r = localRect(trigger);
    const vp = localViewport();
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const fitsAbove = r.top - h - GAP > EDGE;
    const above = side === 'top' ? fitsAbove : !(vp.height - r.bottom - h - GAP > EDGE);
    setPos({
      left: Math.max(EDGE, Math.min(r.left + r.width / 2 - w / 2, vp.width - w - EDGE)),
      top: above ? r.top - h - GAP : r.bottom + GAP,
    });
  }, [side]);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  if (!content) return children;

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <>
      {React.cloneElement(children, {
        ref: triggerRef,
        onMouseEnter: show,
        onMouseLeave: hide,
        onFocus: show,
        onBlur: hide,
      })}
      {open && ReactDOM.createPortal(
        <div
          ref={tipRef}
          role="tooltip"
          className="ui-tooltip"
          style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}

export { Tooltip };
